import { Link } from "react-router-dom";
import { Award, Heart, ShieldCheck, Sparkles, Phone, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND } from "../lib/brand";

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section
        data-testid="about-hero"
        className="relative py-28 bg-[#0F1A14] text-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://images.unsplash.com/photo-1722480419968-a8785b569190?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxiZWF1dGlmdWwlMjBmbG9yaWRhJTIwdHJvcGljYWwlMjBiYWNreWFyZCUyMGdhcmRlbnxlbnwwfHx8fDE3NzgyNjgyODh8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="section-divider mb-6" />
          <p className="uppercase tracking-[0.2em] text-xs text-[#86EFAC] mb-3 font-body">About Us</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-3xl">
            Two decades of caring for South Florida yards.
          </h1>
          <p className="mt-6 text-gray-200 max-w-2xl font-body text-lg">
            E L Lawn Services Inc is a family-owned lawn care and landscaping
            company built on consistent quality, honest service, and a real
            love for the work.
          </p>
        </div>
      </section>

      {/* OUR STORY */}
      <section data-testid="about-story" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-2 items-center">
          <div>
            <div className="section-divider mb-5" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Our story
            </h2>
            <div className="mt-6 space-y-5 text-gray-700 font-body leading-relaxed">
              <p>
                For over {BRAND.experienceYears} years, founder{" "}
                <span className="font-semibold text-gray-900">{BRAND.owner}</span>{" "}
                has been the trusted name behind beautiful lawns and
                landscapes across the Treasure Coast and Palm Beaches.
                What started as a one-truck operation has grown into a
                respected local company — but the values haven't changed.
              </p>
              <p>
                We treat every property like it's our own. That means showing
                up when we say we will, paying attention to the small details,
                and making sure the work speaks for itself. From weekly
                maintenance to full landscape overhauls, our goal is the same:
                a yard you're genuinely proud of.
              </p>
              <p>
                If you're looking for a lawn care partner who actually cares —
                not a faceless company — we'd love to earn your business.
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button
                asChild
                data-testid="about-quote-button"
                className="bg-[#DC2626] hover:bg-red-700 text-white rounded-full px-6 h-11"
              >
                <Link to="/contact">Get a Free Quote</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                data-testid="about-call-button"
                className="border-[#15803D] text-[#15803D] hover:bg-[#15803D] hover:text-white rounded-full px-6 h-11"
              >
                <a href={`tel:${BRAND.phoneTel}`}>
                  <Phone className="mr-2 h-4 w-4" /> {BRAND.phone}
                </a>
              </Button>
            </div>
          </div>

          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1722480419968-a8785b569190?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxiZWF1dGlmdWwlMjBmbG9yaWRhJTIwdHJvcGljYWwlMjBiYWNreWFyZCUyMGdhcmRlbnxlbnwwfHx8fDE3NzgyNjgyODh8MA&ixlib=rb-4.1.0&q=85"
              alt="Florida tropical landscape"
              className="rounded-3xl w-full h-[520px] object-cover"
            />
            <div className="absolute -top-6 -right-6 bg-white border border-gray-200 rounded-2xl shadow-xl px-6 py-5 hidden md:block">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-[#15803D] grid place-items-center text-white">
                  <Award className="h-6 w-6" />
                </div>
                <div>
                  <div className="font-display text-2xl font-bold text-gray-900 leading-none">
                    {BRAND.experienceYears}+
                  </div>
                  <div className="text-xs text-gray-500">Years of Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section data-testid="about-values" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="section-divider mb-6 mx-auto" />
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              What we stand for
            </h2>
            <p className="mt-3 text-gray-600 font-body">
              The principles that have guided our work for two decades.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: ShieldCheck, title: "Reliability", body: "We show up on time, every time. Predictable service you can plan around." },
              { icon: Sparkles, title: "Quality", body: "Sharp edges, clean blowoff, healthy plants. We sweat the details." },
              { icon: Heart, title: "Honesty", body: "Fair pricing, clear communication, and straightforward recommendations." },
              { icon: Award, title: "Experience", body: `${BRAND.experienceYears}+ years of South Florida turf, palm, and landscape know-how.` },
            ].map((v) => (
              <div
                key={v.title}
                data-testid={`value-card-${v.title.toLowerCase()}`}
                className="bg-white border border-gray-200 rounded-2xl p-7 hover:border-[#15803D] hover:-translate-y-1 transition-all"
              >
                <div className="h-12 w-12 rounded-xl bg-[#15803D]/10 grid place-items-center text-[#15803D] mb-4">
                  <v.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-semibold text-gray-900">{v.title}</h3>
                <p className="mt-2 text-sm text-gray-600 font-body leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <section data-testid="about-areas" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 items-center">
            <div>
              <div className="section-divider mb-6" />
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
                Where we work
              </h2>
              <p className="mt-3 text-gray-600 font-body max-w-lg">
                We focus our service area in the communities we know best
                across Palm Beach &amp; Martin counties.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {BRAND.serviceAreas.map((area) => (
                  <span
                    key={area}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-sm text-gray-800 font-medium"
                  >
                    <MapPin className="h-3.5 w-3.5 text-[#DC2626]" />
                    {area}
                  </span>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#15803D] to-[#166534] rounded-3xl p-10 text-white">
              <h3 className="font-display text-2xl font-bold">Not sure if we serve your area?</h3>
              <p className="mt-3 text-white/90 font-body">
                Give us a call or send a quick message — we'll let you know
                right away and get you a free quote.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  asChild
                  data-testid="about-areas-call"
                  className="bg-[#DC2626] hover:bg-red-700 text-white rounded-full px-6"
                >
                  <a href={`tel:${BRAND.phoneTel}`}>
                    <Phone className="mr-2 h-4 w-4" /> {BRAND.phone}
                  </a>
                </Button>
                <Button
                  asChild
                  data-testid="about-areas-contact"
                  variant="outline"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#15803D] rounded-full px-6"
                >
                  <Link to="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
